import React from "react"
import styled from "styled-components"
import { rem } from "polished"
import { media } from "@sr/common"
import {
  BdTitle3,
  SemiBdTitle5,
  RegBody,
  IconArrowLeft,
  SanityImg,
} from "@sr/gatsby-theme-ui"
import SanityImage from "gatsby-plugin-sanity-image"
import { Link } from "gatsby"

export interface CaseStudyEntryHeroPropsShape {
  title: string
  intro: {
    intro: string
    snippet: string
    heroImg: {
      alt: string
      asset: SanityImg
    }
    customerName: string
    customerLogo: {
      alt: string
      asset: SanityImg
    }
    caseStudyEntryKpisList: { label: string; value: string }[]
  }
}

const CaseStudyEntryHero = ({ intro, title }: CaseStudyEntryHeroPropsShape) => {
  const kpis = intro?.caseStudyEntryKpisList
  const { asset, alt } = intro?.customerLogo
  return (
    <CaseStudyEntryHeroStyled>
      <Link className="entry-hero__btn" to="/case-studies/#case-studies__list">
        <IconArrowLeft />
        <p className="entry-hero__btn-text">Back to Case Studies</p>
      </Link>
      <div className="entry-hero__container">
        {intro?.heroImg && intro?.heroImg?.asset && (
          <div className="entry-hero__background-image-container">
            <SanityImage
              alt={intro?.heroImg?.alt || ""}
              asset={intro?.heroImg?.asset}
              className="entry-hero__background-image"
            />
          </div>
        )}
        <div className="entry-hero__content-section">
          {asset && (
            <div className="entry-hero__company-image-container">
              <SanityImage
                alt={alt || ""}
                asset={asset}
                className="entry-hero__company-image"
              />
            </div>
          )}
          <BdTitle3 as="h1" className="entry-hero__content-intro">
            {title}
          </BdTitle3>
          <RegBody className="entry-hero__content-snippet">
            {intro.intro}
          </RegBody>
          {kpis?.length ? (
            <div className="entry-hero__kpi-container">
              {kpis.map((kpi: any, i: number) => {
                return (
                  <div className="entry-hero__kpi" key={i}>
                    <BdTitle3 className="entry-hero__kpi-value">
                      {kpi.value}
                    </BdTitle3>
                    <SemiBdTitle5 className="entry-hero__kpi-label">
                      {kpi.label}
                    </SemiBdTitle5>
                  </div>
                )
              })}
            </div>
          ) : null}
        </div>
      </div>
    </CaseStudyEntryHeroStyled>
  )
}

export default CaseStudyEntryHero

const CaseStudyEntryHeroStyled = styled.section`
  .entry-hero__container {
    display: flex;
    flex-direction: column;
    align-items: center;

    @media ${media.stMidUp} {
      flex-direction: row;
      justify-content: center;
      align-items: center;
      width: ${rem(1232)};
      gap: 58px;
    }
  }

  .entry-hero__btn {
    display: flex;
    align-items: center;
    gap: ${rem(16)};
    height: ${rem(64)};
    padding-left: ${rem(16)};

    @media ${media.midUp} {
      height: ${rem(80)};
      padding-left: ${rem(64)};
    }

    @media ${media.stMidUp} {
      height: ${rem(120)};
      padding-left: 0;
    }
  }

  .entry-hero__btn-text {
    color: var(--color-gray800);
    font-size: ${rem(20)};
  }

  .entry-hero__background-image-container {
    @media ${media.stMidUp} {
      width: ${rem(528)};
      height: ${rem(600)};
    }
  }

  .entry-hero__background-image {
    object-fit: cover;
    width: 100%;
    height: ${rem(384)};

    @media ${media.midUp} {
      height: ${rem(600)};
    }

    @media ${media.stMidUp} {
      border-radius: ${rem(8)};
    }
  }

  .entry-hero__content-section {
    margin-top: ${rem(40)};
    padding: 0 ${rem(16)};

    @media ${media.midUp} {
      padding: 0;
      margin: ${rem(64)} ${rem(16)} 0;
    }

    @media ${media.stMidUp} {
      margin-top: 0;
      display: flex;
      flex-direction: column;
      justify-content: center;
      height: 100%;
      max-width: ${rem(646)};
    }

    .entry-hero__content-intro {
      margin-top: ${rem(32)};

      @media ${media.midUp} {
        margin-top: ${rem(24)};
      }
    }

    .entry-hero__company-image-container {
      height: ${rem(56)};
      @media ${media.stMidUp} {
      }
    }

    .entry-hero__company-image {
      height: 100%;
    }

    .entry-hero__content-intro {
      max-width: ${rem(334)};
      padding-bottom: ${rem(16)};

      @media ${media.midUp} {
        padding-bottom: ${rem(24)};
        font-size: ${rem(40)};
        line-height: ${rem(48)};
        max-width: ${rem(616)};
      }
    }
    .entry-hero__content-snippet {
      @media ${media.midUp} {
        max-width: ${rem(616)};
      }

      @media ${media.stMidUp} {
        max-width: ${rem(642)};
      }
    }

    .entry-hero__kpi-container {
      display: grid;
      grid-template-columns: repeat(1, 1fr);
      grid-auto-rows: minmax(auto, max-content);
      gap: ${rem(32)} 0;
      padding: ${rem(32)} 0 ${rem(40)} 0;

      @media ${media.midUp} {
        grid-template-columns: repeat(2, 1fr);
        gap: ${rem(40)} ${rem(72)};
      }

      @media ${media.stMidUp} {
        padding: ${rem(24)} 0;
      }

      .entry-hero__kpi {
        display: flex;
        flex-direction: column;

        @media ${media.stMidUp} {
          flex-direction: column;
          align-items: flex-start;
        }

        .entry-hero__kpi-label {
          font-size: ${rem(12)};
          line-height: ${rem(16)};
          padding-top: ${rem(4)};

          @media ${media.midUp} {
            padding-top: ${rem(0)};
            font-size: ${rem(20)};
            line-height: ${rem(24)};
          }

          @media ${media.stMidUp} {
            font-size: ${rem(16)};
            line-height: ${rem(24)};
          }
        }
        .entry-hero__kpi-value {
          font-size: ${rem(40)};
          color: var(--color-primary);
        }
      }
    }
  }
`
